/*
  DeletedItems — admin-only recovery screen for soft-deleted clinical
  records. Three tabs (Notes | Assessments | Documents), each fed by
  a dedicated backend endpoint:
      GET /api/soap-notes/deleted-notes
      GET /api/deleted-assessments
      GET /api/health-record/deleted-attachments
  Each row shows what was deleted, by whom, when, and the reason.
  Managers/owners can click Restore to bring it back into the active
  workspace — the target endpoints round-trip and refresh the list.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Trash2, RotateCcw, ClipboardList, FileText, Paperclip,
  RefreshCw, User as UserIcon, Search, Loader2,
} from "lucide-react";

function fmt(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts).slice(0, 19).replace("T", " ");
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function DeletedRow({ icon: Icon, title, subtitle, deletedAt, deletedBy, reason, onRestore, testid }) {
  const [busy, setBusy] = useState(false);
  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-zinc-200 bg-white"
      data-testid={testid}
    >
      <div className="h-8 w-8 rounded-md border border-red-100 bg-red-50 text-red-600 grid place-items-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-900 truncate">{title}</div>
        {subtitle && (
          <div className="text-xs text-zinc-600 truncate">{subtitle}</div>
        )}
        <div className="mt-1 text-2xs text-zinc-500 flex items-center gap-3 flex-wrap font-mono-num">
          <span>Deleted {fmt(deletedAt)}</span>
          {deletedBy && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" /> {deletedBy}
            </span>
          )}
        </div>
        {reason && (
          <div className="mt-1 text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded px-2 py-1">
            <span className="text-zinc-500">Reason:</span> {reason}
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={async () => {
          setBusy(true);
          try {
            await onRestore();
            toast.success("Restored");
          } catch (e) {
            toast.error("Restore failed", {
              description: e?.response?.data?.detail || e.message,
            });
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy}
        data-testid={`${testid}-restore`}
      >
        {busy ? (
          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Restoring…</>
        ) : (
          <><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</>
        )}
      </Button>
    </div>
  );
}

function useDeletedFeed(url) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(url);
      const data = Array.isArray(r.data) ? r.data : (r.data?.attachments || []);
      setRows(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [url]);
  useEffect(() => { load(); }, [load]);
  return { rows, loading, err, reload: load };
}

function matches(row, q) {
  if (!q) return true;
  const hay = [
    row.title, row.name, row.client_name, row.client_email, row.appointment_type,
    row.filename, row.description, row.deleted_reason, row.deleted_by_email,
    row.category,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function DeletedItems() {
  const [q, setQ] = useState("");

  const notes = useDeletedFeed("/soap-notes/deleted-notes");
  const assessments = useDeletedFeed("/deleted-assessments");
  const attachments = useDeletedFeed("/health-record/deleted-attachments");

  const filteredNotes = notes.rows.filter((r) => matches(r, q));
  const filteredAssessments = assessments.rows.filter((r) => matches(r, q));
  const filteredAttachments = attachments.rows.filter((r) => matches(r, q));

  const totalDeleted =
    notes.rows.length + assessments.rows.length + attachments.rows.length;

  async function restoreNote(id) {
    await api.post(`/soap-notes/notes/${id}/restore`);
    await notes.reload();
  }
  async function restoreAssessment(id) {
    await api.post(`/assessments/${id}/restore`);
    await assessments.reload();
  }
  async function restoreAttachment(id) {
    await api.post(`/health-record/attachments/${id}/restore`);
    await attachments.reload();
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="deleted-items-page">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-red-50 border border-red-100 p-2">
          <Trash2 className="h-4 w-4 text-red-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-zinc-900">Deleted items</h1>
          <p className="text-sm text-zinc-600 mt-0.5">
            Soft-deleted SOAP notes, assessments and documents live here for
            recovery. Click <span className="font-medium">Restore</span> to bring
            an item back into the client workspace. Hard-delete / purge is not
            offered from the UI — records are preserved for audit.
          </p>
          <div className="mt-1 text-2xs text-zinc-500">
            {totalDeleted} deleted item{totalDeleted === 1 ? "" : "s"} in total.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { notes.reload(); assessments.reload(); attachments.reload(); }}
          data-testid="deleted-items-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Refresh
        </Button>
      </div>

      <Card className="p-3">
        <div className="relative">
          <Search className="h-4 w-4 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by client, reason, actor or filename"
            className="pl-8 h-9"
            data-testid="deleted-items-search"
          />
        </div>
      </Card>

      <Tabs defaultValue="notes">
        <TabsList data-testid="deleted-items-tabs">
          <TabsTrigger value="notes" data-testid="deleted-items-tab-notes">
            <FileText className="h-3.5 w-3.5 mr-1" />
            Notes ({filteredNotes.length})
          </TabsTrigger>
          <TabsTrigger value="assessments" data-testid="deleted-items-tab-assessments">
            <ClipboardList className="h-3.5 w-3.5 mr-1" />
            Assessments ({filteredAssessments.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="deleted-items-tab-documents">
            <Paperclip className="h-3.5 w-3.5 mr-1" />
            Documents ({filteredAttachments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notes" className="mt-3 space-y-2">
          {notes.loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
            </div>
          ) : notes.err ? (
            <div className="text-sm text-red-700 py-6 text-center">{notes.err}</div>
          ) : filteredNotes.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              No deleted SOAP notes.
            </div>
          ) : (
            filteredNotes.map((r) => (
              <DeletedRow
                key={r.id}
                icon={FileText}
                title={r.client_name || r.client_email || "(untitled note)"}
                subtitle={
                  [r.appointment_type, r.created_at?.slice(0, 10)]
                    .filter(Boolean).join(" · ")
                }
                deletedAt={r.deleted_at}
                deletedBy={r.deleted_by_email || r.deleted_by}
                reason={r.deleted_reason}
                onRestore={() => restoreNote(r.id)}
                testid={`deleted-note-${r.id}`}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="assessments" className="mt-3 space-y-2">
          {assessments.loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
            </div>
          ) : assessments.err ? (
            <div className="text-sm text-red-700 py-6 text-center">{assessments.err}</div>
          ) : filteredAssessments.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              No deleted assessments.
            </div>
          ) : (
            filteredAssessments.map((r) => (
              <DeletedRow
                key={r.id}
                icon={ClipboardList}
                title={r.client_name || r.client_email || "(untitled assessment)"}
                subtitle={
                  [r.status, r.started_at?.slice(0, 10)]
                    .filter(Boolean).join(" · ")
                }
                deletedAt={r.deleted_at}
                deletedBy={r.deleted_by_email || r.deleted_by}
                reason={r.deleted_reason}
                onRestore={() => restoreAssessment(r.id)}
                testid={`deleted-assessment-${r.id}`}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-3 space-y-2">
          {attachments.loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
            </div>
          ) : attachments.err ? (
            <div className="text-sm text-red-700 py-6 text-center">{attachments.err}</div>
          ) : filteredAttachments.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              No deleted documents.
            </div>
          ) : (
            filteredAttachments.map((r) => (
              <DeletedRow
                key={r.id}
                icon={Paperclip}
                title={r.filename || r.description || "(untitled document)"}
                subtitle={
                  [r.client_email, r.category, r.created_at?.slice(0, 10)]
                    .filter(Boolean).join(" · ")
                }
                deletedAt={r.deleted_at}
                deletedBy={r.deleted_by_email || r.deleted_by}
                reason={r.deleted_reason}
                onRestore={() => restoreAttachment(r.id)}
                testid={`deleted-attachment-${r.id}`}
              />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
